import React, { useState, useEffect } from 'react';
import { getAdminStats, getUsers, getAllTransactions, getSubadmins, getApiStatus, updateApiStatus } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Users,
  Smartphone,
  DollarSign,
  TrendingUp,
  Activity,
  Wallet,
  UserCog,
  Power,
  ShieldAlert,
} from 'lucide-react';
import { toast } from 'sonner';
import { TelegramPrefsCard } from '../../components/admin/TelegramPrefsCard';

const StatCard = ({ title, value, icon: Icon, subtitle, color = 'primary' }) => (
  <Card className="bg-card border-border/40 hover:border-primary/30 transition-colors">
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground font-medium uppercase tracking-wider">
            {title}
          </p>
          <p className={`text-4xl font-black tracking-tighter mt-2 metric-value text-${color}`}>
            {value}
          </p>
          {subtitle && (
            <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
          )}
        </div>
        <div className={`p-3 rounded-lg bg-${color}/10`}>
          <Icon className={`w-6 h-6 text-${color}`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function AdminDashboard() {
  const { user, isAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentUsers, setRecentUsers] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiActive, setApiActive] = useState(true);
  const [togglingApi, setTogglingApi] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, usersRes, transRes, apiStatusRes] = await Promise.all([
          getAdminStats(),
          getUsers(),
          getAllTransactions(),
          getApiStatus(),
        ]);
        setStats(statsRes.data);
        setRecentUsers(usersRes.data.slice(0, 5));
        setRecentTransactions(transRes.data.slice(0, 5));
        setApiActive(apiStatusRes.data.is_active);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleToggleApi = async (checked) => {
    setTogglingApi(true);
    try {
      const res = await updateApiStatus(checked);
      setApiActive(res.data.is_active);
      toast.success(checked ? 'API activada correctamente' : 'API desactivada correctamente');
    } catch (err) {
      toast.error('Error al cambiar el estado de la API');
    } finally {
      setTogglingApi(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  const isSubadmin = user?.role === 'subadmin';

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <h1 className="text-4xl font-black tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground mt-1">
          {isSubadmin ? 'Panel de Subadministrador' : 'Resumen del sistema de recargas'}
        </p>
      </div>

      {/* API Control - Only visible to Admin */}
      {isAdmin && (
        <Card
          className={`border-2 transition-colors ${
            apiActive
              ? 'border-emerald-500/40 bg-emerald-500/5'
              : 'border-destructive/40 bg-destructive/5'
          }`}
          data-testid="api-status-card"
        >
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div
                  className={`p-3 rounded-lg ${
                    apiActive ? 'bg-emerald-500/15' : 'bg-destructive/15'
                  }`}
                >
                  {apiActive ? (
                    <Power className="w-6 h-6 text-emerald-500" />
                  ) : (
                    <ShieldAlert className="w-6 h-6 text-destructive" />
                  )}
                </div>
                <div>
                  <p className="font-bold text-base">Estado de la API de Recargas</p>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {apiActive
                      ? 'La API está activa. Las recargas están habilitadas para todos los usuarios.'
                      : 'La API está desactivada. Ninguna recarga podrá realizarse en el sistema.'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <Label
                  htmlFor="api-toggle"
                  className={`text-sm font-semibold ${
                    apiActive ? 'text-emerald-500' : 'text-destructive'
                  }`}
                >
                  {apiActive ? 'Activa' : 'Inactiva'}
                </Label>
                <Switch
                  id="api-toggle"
                  checked={apiActive}
                  onCheckedChange={handleToggleApi}
                  disabled={togglingApi}
                  data-testid="api-toggle-switch"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {isAdmin && <TelegramPrefsCard />}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {isAdmin && (
          <StatCard
            title="Subadministradores"
            value={stats?.total_subadmins || 0}
            icon={UserCog}
            color="primary"
          />
        )}
        <StatCard
          title="Usuarios Activos"
          value={stats?.active_users || 0}
          subtitle={`de ${stats?.total_users || 0} totales`}
          icon={Users}
          color="primary"
        />
        <StatCard
          title="Servicios Activos"
          value={stats?.active_services || 0}
          subtitle={isAdmin ? `de ${stats?.total_services || 0} totales` : null}
          icon={Smartphone}
          color="accent"
        />
        <StatCard
          title="Total Transacciones"
          value={stats?.total_transactions || 0}
          icon={Activity}
          color="primary"
        />
        <StatCard
          title="Ingresos Totales"
          value={formatCurrency(stats?.total_revenue || 0)}
          icon={DollarSign}
          color="accent"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Users */}
        <Card className="bg-card border-border/40">
          <CardHeader>
            <CardTitle className="text-xl font-bold flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Usuarios Recientes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentUsers.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                No hay usuarios registrados
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50">
                    <TableHead>Usuario</TableHead>
                    <TableHead>Saldo</TableHead>
                    <TableHead>Descuento</TableHead>
                    <TableHead>Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentUsers.map((user) => (
                    <TableRow
                      key={user.id}
                      className="border-border/30 hover:bg-secondary/30"
                    >
                      <TableCell>
                        <div>
                          <p className="font-medium">{user.username}</p>
                          <p className="text-xs text-muted-foreground">{user.email}</p>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-accent">
                        {formatCurrency(user.balance)}
                      </TableCell>
                      <TableCell className="font-mono text-primary">
                        {user.discount_percentage}%
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={user.is_active ? 'status-active' : 'status-inactive'}
                        >
                          {user.is_active ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Recent Transactions */}
        <Card className="bg-card border-border/40">
          <CardHeader>
            <CardTitle className="text-xl font-bold flex items-center gap-2">
              <Activity className="w-5 h-5 text-accent" />
              Transacciones Recientes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentTransactions.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                No hay transacciones
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50">
                    <TableHead>Usuario</TableHead>
                    <TableHead>Servicio</TableHead>
                    <TableHead>Monto</TableHead>
                    <TableHead>Fecha</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentTransactions.map((tx) => (
                    <TableRow
                      key={tx.id}
                      className="border-border/30 hover:bg-secondary/30"
                    >
                      <TableCell className="font-medium">{tx.username}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {tx.service_name}
                      </TableCell>
                      <TableCell className="font-mono text-accent">
                        {formatCurrency(tx.final_price)}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {formatDate(tx.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Summary Card */}
      <Card className="bg-card border-border/40">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-4 rounded-lg bg-accent/10">
                <Wallet className="w-8 h-8 text-accent" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground uppercase tracking-wider">
                  Saldo Total Distribuido
                </p>
                <p className="text-3xl font-black tracking-tighter text-accent metric-value">
                  {formatCurrency(stats?.total_balance_distributed || 0)}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">
                Saldo disponible para usuarios
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
